import { Button } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { useSendLicenseInvoiceMutation } from "Api/Mutations/Licenses/useSendLicenseInvoiceMutation";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { LicensePaymentStatus } from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const CONTROLS_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Licenses.Form.Controls;

type Props = {
  licensePublicID: string;
  partyPublicID: string;
  invoiceEmailSent: boolean;
  paymentDate: string | null | undefined;
  paymentStatus: LicensePaymentStatus;
  isDirty: boolean;
};

export const SendInvoiceButton = ({
  licensePublicID,
  partyPublicID,
  invoiceEmailSent,
  paymentDate,
  paymentStatus,
  isDirty,
}: Props) => {
  const { t } = useResource();
  const queryClient = useQueryClient();

  const { mutate: sendLicenseInvoice, isPending: isSendingInvoice } =
    useSendLicenseInvoiceMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(partyPublicID),
        });
        toast.success(t(CONTROLS_RESOURCES.SendInvoiceSuccess));
      },
      onError: () => {
        toast.error(t(CONTROLS_RESOURCES.SendInvoiceError));
      },
    });

  const handleSendInvoice = () => {
    sendLicenseInvoice({ licensePublicID });
  };

  const isDisabled =
    !paymentDate || paymentStatus !== LicensePaymentStatus.Success || isDirty;

  return (
    <Button
      variant="outlined"
      color={invoiceEmailSent ? "success" : "primary"}
      onClick={handleSendInvoice}
      disabled={isDisabled || isSendingInvoice}
      startIcon={
        invoiceEmailSent && !isSendingInvoice ? <CheckCircle /> : undefined
      }
      sx={{ minWidth: "160px", height: "44px" }}
    >
      {isSendingInvoice
        ? t(CONTROLS_RESOURCES.SendingInvoice)
        : t(CONTROLS_RESOURCES.SendInvoice)}
    </Button>
  );
};
